import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMoney, localIsoDate } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Badge, Field, Input, Select, Textarea, useToast } from '../components/ui';
import { ReceiptPicker, useReceiptUpload } from '../components/ReceiptPicker';
import { Direction, TX_GROUPS, formatShare, type PartnerPosition, type PartnerTx, type TxType } from './Partners';

/**
 * Partner forms (Finance Phase 1, 2026-10-28) — every one a page, as every form
 * in Finance is: a partner, a change of share, a transaction, a reversal.
 */

/** A refusal in the reader's language when Finance has words for its code, the API's otherwise. */
export function useRefusal(prefix: string) {
  const { t } = useI18n();
  return (e: unknown) => {
    if (e instanceof ApiError) {
      const key = `${prefix}.error.${e.code}` as TranslationKey;
      const known = t(key);
      return known === key ? e.message : `${known} ${e.message}`;
    }
    return e instanceof Error ? e.message : String(e);
  };
}

/** The field a refusal names, so the form can show it beside that field. */
const fieldOf = (e: unknown): string | null =>
  e instanceof ApiError ? ((e.details as { field?: string } | undefined)?.field ?? null) : null;

interface Currency {
  code: string;
  isBase: boolean;
  isActive: boolean;
  rateToBase: number | null;
}

// ---------------------------------------------------------------------------
// A partner
// ---------------------------------------------------------------------------

/** Routes: `/partners/new`, `/partners/:id/edit`. */
export function PartnerFormPage() {
  const { id } = useParams<{ id: string }>();
  const editing = !!id;
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('partners');

  const existing = useQuery({
    queryKey: ['partners', id],
    queryFn: () => api.get<any>(`/partners/${id}`),
    enabled: editing,
  });

  const [form, setForm] = useState({
    name: '',
    nameAr: '',
    email: '',
    phone: '',
    notes: '',
    status: 'ACTIVE',
    percentage: '',
    effectiveFrom: localIsoDate(),
  });
  useEffect(() => {
    const p = existing.data;
    if (p) setForm((f) => ({ ...f, name: p.name, nameAr: p.nameAr ?? '', email: p.email ?? '', phone: p.phone ?? '', notes: p.notes ?? '', status: p.status }));
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () =>
      editing
        ? api.patch<any>(`/partners/${id}`, {
            name: form.name,
            nameAr: form.nameAr || null,
            email: form.email || null,
            phone: form.phone || null,
            notes: form.notes || null,
            status: form.status,
          })
        : api.post<any>('/partners', {
            name: form.name,
            nameAr: form.nameAr || undefined,
            email: form.email || undefined,
            phone: form.phone || undefined,
            notes: form.notes || undefined,
            share: { percentage: Number(form.percentage), effectiveFrom: form.effectiveFrom },
          }),
    onSuccess: (partner) => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
      navigate(`/partners/${partner.id}`);
    },
    onError: (e) => toast.error(refusal(e)),
  });

  const pct = Number(form.percentage);
  const shareOk = editing || (form.percentage !== '' && pct >= 0 && pct <= 100);

  return (
    <FormPage
      title={editing ? t('partners.edit') : t('partners.new')}
      backTo={editing ? `/partners/${id}` : '/partners'}
      backLabel={t('partners.title')}
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={!form.name.trim() || !shareOk}
      loading={editing && existing.isLoading}
      error={existing.error}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('partners.name')} required>
          <Input value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={t('partners.nameAr')}>
          <Input dir="rtl" value={form.nameAr} maxLength={120} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('common.email')}>
          <Input type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label={t('common.phone')}>
          <Input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        {!editing && (
          <>
            <Field label={t('partners.share')} required hint={t('partners.shareHint')}>
              <Input type="number" min={0} max={100} step="0.000001" dir="ltr" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} />
            </Field>
            <Field label={t('partners.shareStarts')} required>
              <Input type="date" value={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} />
            </Field>
          </>
        )}
        {editing && (
          <Field label={t('partners.status')} hint={t('partners.inactiveHint')}>
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="ACTIVE">{t('partners.active')}</option>
              <option value="INACTIVE">{t('partners.inactive')}</option>
            </Select>
          </Field>
        )}
        <Field label={t('common.notes')} className="sm:col-span-2">
          <Textarea dir="auto" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/** Route: `/partners/:id/shares/new`. */
export function PartnerShareFormPage() {
  const { id } = useParams<{ id: string }>();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('partners');
  const partner = useQuery({ queryKey: ['partners', id], queryFn: () => api.get<any>(`/partners/${id}`) });
  const latest = partner.data?.shares?.at(-1) as { percentage: string; effectiveFrom: string } | undefined;
  const [form, setForm] = useState({ percentage: '', effectiveFrom: localIsoDate(), note: '' });

  const save = useMutation({
    mutationFn: () => api.post(`/partners/${id}/shares`, { percentage: Number(form.percentage), effectiveFrom: form.effectiveFrom, note: form.note || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
      navigate(`/partners/${id}`);
    },
    onError: (e) => toast.error(refusal(e)),
  });

  const pct = Number(form.percentage);
  return (
    <FormPage
      title={t('partners.changeShare')}
      subtitle={partner.data ? localized(partner.data) : undefined}
      backTo={`/partners/${id}`}
      backLabel={partner.data ? localized(partner.data) : t('partners.title')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={form.percentage === '' || pct < 0 || pct > 100 || (!!latest && form.effectiveFrom < latest.effectiveFrom)}
      loading={partner.isLoading}
      error={partner.error}
      onSubmit={() => save.mutate()}
      summary={
        latest && (
          <p className="text-body-sm">
            {t('partners.shareToday')}: <span className="tabular">{formatShare(partner.data.share, locale)}</span> ·{' '}
            {t('partners.shareForward', { date: formatDate(latest.effectiveFrom, locale) })}
          </p>
        )
      }
    >
      <FormGrid>
        <Field label={t('partners.share')} required hint={t('partners.shareHint')}>
          <Input type="number" min={0} max={100} step="0.000001" dir="ltr" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} autoFocus />
        </Field>
        <Field label={t('partners.shareStarts')} required>
          <Input type="date" min={latest?.effectiveFrom} value={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} />
        </Field>
        <Field label={t('partners.shareNote')} className="sm:col-span-2">
          <Input dir="auto" value={form.note} maxLength={500} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

// ---------------------------------------------------------------------------
// A transaction
// ---------------------------------------------------------------------------

interface ExpenseOption {
  id: string;
  expenseDate: string;
  description: string;
  vendorName: string | null;
  amount: string;
  currency: string;
  status: string;
  remaining: string;
  category: { name: string; nameAr: string | null } | null;
}

/** Route: `/partner-transactions/new` (`?partnerId=`, `?type=`). */
export function PartnerTransactionFormPage() {
  const [params] = useSearchParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('ptx');
  const receipt = useReceiptUpload();
  // One per form: sent twice, the transaction is recorded once.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const [form, setForm] = useState({
    partnerId: params.get('partnerId') ?? '',
    type: (params.get('type') as TxType | null) ?? ('LOAN_RECEIVED' as TxType),
    transactionDate: localIsoDate(),
    amount: '',
    currency: '',
    exchangeRate: '',
    moneyAccountId: '',
    expenseId: '',
    fundingType: 'LOAN',
    adjustedBalance: 'LOAN_PAYABLE',
    adjustmentDirection: 'DECREASE',
    reference: '',
    notes: '',
  });
  const [expenseQuery, setExpenseQuery] = useState('');
  const [failedField, setFailedField] = useState<string | null>(null);

  const partners = useQuery({ queryKey: ['partners', '', ''], queryFn: () => api.get<any>('/partners') }).data;
  const currencies = useQuery({ queryKey: ['currencies'], queryFn: () => api.get<Currency[]>('/currencies') }).data ?? [];
  const accounts = useQuery({ queryKey: ['money-accounts', 'active'], queryFn: () => api.get<any[]>('/money-accounts', { activeOnly: true }) }).data ?? [];
  const partner = useQuery({
    queryKey: ['partners', form.partnerId],
    queryFn: () => api.get<any>(`/partners/${form.partnerId}`),
    enabled: !!form.partnerId,
  }).data;

  const base = currencies.find((c) => c.isBase)?.code ?? '';
  const currency = form.currency || base;
  const foreign = !!currency && !!base && currency !== base;
  const todayRate = currencies.find((c) => c.code === currency)?.rateToBase ?? null;
  const moves = ['CAPITAL_CONTRIBUTION', 'LOAN_RECEIVED', 'LOAN_REPAYMENT', 'ADVANCE_PAID', 'ADVANCE_REPAYMENT', 'PROFIT_DISTRIBUTION'].includes(form.type);
  const paidExpense = form.type === 'PAID_COMPANY_EXPENSE';
  const adjustment = form.type === 'ADJUSTMENT';
  const accountsHere = accounts.filter((a) => a.currency === currency);
  const cashDirection = (['CAPITAL_CONTRIBUTION', 'LOAN_RECEIVED', 'ADVANCE_REPAYMENT'].includes(form.type) ? 1 : moves ? -1 : 0) as 1 | -1 | 0;
  const cutover = partner?.cutover?.status === 'CONFIRMED' ? (partner.cutover.cutoverDate as string) : null;
  const beforeCutover = !!cutover && form.transactionDate < cutover;
  const position = (partner?.positions as PartnerPosition[] | undefined)?.find((p) => p.currency === currency);

  const expenses = useQuery({
    queryKey: ['partner-transactions', 'expense-options', currency, expenseQuery],
    queryFn: () => api.get<ExpenseOption[]>('/partner-transactions/expense-options', { currency, q: expenseQuery }),
    enabled: paidExpense && !!currency,
  }).data;
  const chosenExpense = expenses?.find((e) => e.id === form.expenseId);

  // The rate starts at today's only for a transaction dated today; history keeps the rate of its own day.
  useEffect(() => {
    if (!foreign) setForm((f) => ({ ...f, exchangeRate: '' }));
    else if (form.transactionDate === localIsoDate() && todayRate) setForm((f) => (f.exchangeRate ? f : { ...f, exchangeRate: String(todayRate) }));
  }, [foreign, todayRate, form.transactionDate]);

  // An account in another currency, or one for a type that moves none, is dropped.
  useEffect(() => {
    if (!moves || (form.moneyAccountId && !accountsHere.some((a) => a.id === form.moneyAccountId))) setForm((f) => ({ ...f, moneyAccountId: '' }));
  }, [moves, currency, accountsHere.length]);

  const save = useMutation({
    mutationFn: () =>
      api.post<PartnerTx>('/partner-transactions', {
        partnerId: form.partnerId,
        type: form.type,
        transactionDate: form.transactionDate,
        amount: Number(form.amount),
        currency,
        exchangeRate: foreign ? Number(form.exchangeRate) : undefined,
        moneyAccountId: moves ? form.moneyAccountId : undefined,
        expenseId: paidExpense ? form.expenseId : undefined,
        fundingType: paidExpense ? form.fundingType : undefined,
        adjustedBalance: adjustment ? form.adjustedBalance : undefined,
        adjustmentDirection: adjustment ? form.adjustmentDirection : undefined,
        reference: form.reference || undefined,
        notes: form.notes || undefined,
        attachmentId: receipt.file?.id,
        idempotencyKey,
      }),
    onSuccess: (tx) => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
      void queryClient.invalidateQueries({ queryKey: ['partner-transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['financial-setup'] });
      toast.success(t('ptx.saved'));
      navigate(`/partner-transactions/${tx.id}`);
    },
    onError: (e) => {
      setFailedField(fieldOf(e));
      toast.error(refusal(e));
    },
  });

  const amount = Number(form.amount);
  const ready =
    !!form.partnerId &&
    amount > 0 &&
    !!currency &&
    (!foreign || Number(form.exchangeRate) > 0) &&
    (!moves || !!form.moneyAccountId) &&
    (!paidExpense || (!!form.expenseId && (!chosenExpense || amount <= Number(chosenExpense.remaining)))) &&
    (!adjustment || !!form.notes.trim()) &&
    !receipt.busy;

  const types = TX_GROUPS.map((g) => ({ ...g, types: g.types.filter((type) => type !== 'ADJUSTMENT' || can(P.PARTNERS_REVERSE)) }));
  const err = (field: string) => (failedField === field ? t('common.required') : null);

  return (
    <FormPage
      title={t('ptx.new')}
      backTo={form.partnerId ? `/partners/${form.partnerId}` : '/partner-transactions'}
      backLabel={partner ? localized(partner) : t('ptx.title')}
      submitLabel={t('partners.recordTransaction')}
      submitting={save.isPending}
      disabled={!ready}
      onSubmit={() => save.mutate()}
      width="lg"
    >
      <fieldset className="mb-6">
        <legend className="mb-3 text-body-sm font-medium text-[var(--text-secondary)]">{t('ptx.type')}</legend>
        {/* Every card one size: the groups take their rows from this one grid (subgrid) — stacked on a phone, side by side from lg — and every row of cards is 1fr, as tall as the tallest card. */}
        <div
          className="grid grid-rows-[var(--stacked)] gap-x-4 gap-y-2 lg:grid-cols-3 lg:grid-rows-[auto_repeat(var(--cards),1fr)]"
          style={
            {
              '--stacked': types.map((g) => `auto repeat(${g.types.length},1fr)`).join(' '),
              '--cards': Math.max(...types.map((g) => g.types.length)),
            } as CSSProperties
          }
        >
          {types.map((group, i) => (
            <div
              key={group.label}
              className="row-[span_var(--rows)] grid grid-rows-subgrid lg:row-span-full"
              style={{ '--rows': group.types.length + 1 } as CSSProperties}
            >
              <p className={clsx('text-overline uppercase text-[var(--text-tertiary)]', i > 0 && 'mt-2 lg:mt-0')}>{t(group.label)}</p>
              {group.types.map((type) => (
                <label
                  key={type}
                  className={clsx(
                    'flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2.5 transition-colors',
                    form.type === type ? 'border-primary bg-primary/5' : 'hairline hover:border-primary/50',
                  )}
                >
                  <input type="radio" name="type" className="mt-1 accent-[#C7102E]" checked={form.type === type} onChange={() => setForm({ ...form, type })} />
                  <span>
                    <span className="block text-body-sm font-medium text-[var(--text-primary)]">{t(`ptx.${type}` as TranslationKey)}</span>
                    <span className="block text-caption text-muted">{t(`ptx.${type}.hint` as TranslationKey)}</span>
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      </fieldset>

      <FormGrid>
        <Field label={t('ptx.partner')} required>
          <Select value={form.partnerId} onChange={(e) => setForm({ ...form, partnerId: e.target.value })}>
            <option value="">—</option>
            {partners?.items.map((p: any) => (
              <option key={p.id} value={p.id}>
                {localized(p)}
                {p.status === 'INACTIVE' ? ` (${t('partners.inactive')})` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('ptx.date')} required error={err('transactionDate')}>
          <Input type="date" max={localIsoDate()} value={form.transactionDate} onChange={(e) => setForm({ ...form, transactionDate: e.target.value })} />
        </Field>
        <Field label={t('ptx.amount')} required error={err('amount')}>
          <Input type="number" min={0.01} step="0.01" dir="ltr" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </Field>
        <Field label={t('common.currency')} required>
          <Select value={currency} onChange={(e) => setForm({ ...form, currency: e.target.value, exchangeRate: '', expenseId: '' })}>
            {currencies.filter((c) => c.isActive).map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
        </Field>
        {foreign && (
          <Field
            label={t('ptx.rate', { base })}
            required
            error={err('exchangeRate')}
            hint={todayRate ? t('ptx.rateHint', { rate: String(todayRate) }) : t('ptx.rateNone')}
          >
            <Input type="number" min={0} step="0.00000001" dir="ltr" value={form.exchangeRate} onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })} />
          </Field>
        )}
        {foreign && Number(form.exchangeRate) > 0 && amount > 0 && (
          <Field label={t('ptx.baseValue', { base })}>
            <p className="h-10 py-2 tabular text-[var(--text-primary)]">{formatMoney(amount * Number(form.exchangeRate), base, locale)}</p>
          </Field>
        )}

        {moves && (
          <Field
            label={t('ptx.account')}
            required
            className="sm:col-span-2"
            error={err('moneyAccountId')}
            hint={accountsHere.length ? t('ptx.accountHint') : t('ptx.noAccounts', { currency })}
          >
            <Select value={form.moneyAccountId} onChange={(e) => setForm({ ...form, moneyAccountId: e.target.value })}>
              <option value="">—</option>
              {accountsHere.map((a) => (
                <option key={a.id} value={a.id}>
                  {localized(a)} · {t(a.type === 'BANK' ? 'setup.accountBANK' : 'setup.accountCASH')}
                  {a.reference ? ` · ${a.reference}` : ''}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {paidExpense && (
          <>
            <Field label={t('ptx.expenseSearch')} className="sm:col-span-2">
              <Input value={expenseQuery} onChange={(e) => setExpenseQuery(e.target.value)} placeholder={t('common.search')} />
            </Field>
            <Field label={t('ptx.expense')} required className="sm:col-span-2" error={err('expenseId')} hint={t('ptx.expenseHint')}>
              <Select value={form.expenseId} onChange={(e) => setForm({ ...form, expenseId: e.target.value })}>
                <option value="">{expenses && expenses.length === 0 ? t('ptx.noExpenses', { currency }) : '—'}</option>
                {expenses?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {formatDate(e.expenseDate, locale)} · {e.description} · {formatMoney(e.amount, e.currency, locale)} —{' '}
                    {t('ptx.expenseRemaining', { amount: formatMoney(e.remaining, e.currency, locale) })}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('ptx.funding')} required>
              <Select value={form.fundingType} onChange={(e) => setForm({ ...form, fundingType: e.target.value })}>
                <option value="LOAN">{t('ptx.fundingLOAN')}</option>
                <option value="CAPITAL">{t('ptx.fundingCAPITAL')}</option>
              </Select>
            </Field>
          </>
        )}

        {adjustment && (
          <>
            <Field label={t('ptx.balance')} required>
              <Select value={form.adjustedBalance} onChange={(e) => setForm({ ...form, adjustedBalance: e.target.value })}>
                {['LOAN_PAYABLE', 'ADVANCE_RECEIVABLE', 'EQUITY'].map((b) => (
                  <option key={b} value={b}>{t(`ptx.adjusted${b}` as TranslationKey)}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('ptx.direction')} required>
              <Select value={form.adjustmentDirection} onChange={(e) => setForm({ ...form, adjustmentDirection: e.target.value })}>
                <option value="DECREASE">{t('ptx.DECREASE')}</option>
                <option value="INCREASE">{t('ptx.INCREASE')}</option>
              </Select>
            </Field>
          </>
        )}

        <Field label={t('ptx.reference')}>
          <Input dir="auto" value={form.reference} maxLength={120} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        </Field>
        <Field label={t('ptx.evidence')}>
          <ReceiptPicker receipt={receipt} />
        </Field>
        <Field label={adjustment ? t('ptx.reason') : t('ptx.notes')} required={adjustment} className="sm:col-span-2" error={err('notes')}>
          <Textarea dir="auto" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </FormGrid>

      <div className="mt-5 space-y-2 rounded-md bg-[var(--surface-sunken)] px-4 py-3 text-body-sm">
        <p><Direction value={cashDirection} /></p>
        {position && (
          <p className="text-muted">
            {t('ptx.nowOwed', {
              loan: formatMoney(position.loanPayable, currency, locale),
              advance: formatMoney(position.advanceReceivable, currency, locale),
            })}
          </p>
        )}
        {beforeCutover && (
          <p className="flex items-start gap-2">
            <Badge tone="warning">{t('ptx.history')}</Badge>
            <span>{t('ptx.beforeCutover', { date: formatDate(cutover, locale) })}</span>
          </p>
        )}
      </div>
    </FormPage>
  );
}

/** Route: `/partner-transactions/:id/reverse`. */
export function PartnerTransactionReversePage() {
  const { id } = useParams<{ id: string }>();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refusal = useRefusal('ptx');
  const [reason, setReason] = useState('');
  const tx = useQuery({ queryKey: ['partner-transactions', id], queryFn: () => api.get<PartnerTx>(`/partner-transactions/${id}`) });

  const reverse = useMutation({
    mutationFn: () => api.post(`/partner-transactions/${id}/reverse`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
      void queryClient.invalidateQueries({ queryKey: ['partner-transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['financial-setup'] });
      navigate(`/partner-transactions/${id}`);
    },
    onError: (e) => toast.error(refusal(e)),
  });

  const data = tx.data;
  return (
    <FormPage
      title={t('ptx.reverseTitle', { number: data?.number ?? '' })}
      subtitle={t('ptx.reverseHint')}
      backTo={`/partner-transactions/${id}`}
      backLabel={data?.number ?? t('ptx.title')}
      submitLabel={t('ptx.reverse')}
      destructive
      submitting={reverse.isPending}
      disabled={!reason.trim()}
      loading={tx.isLoading}
      error={tx.error}
      onSubmit={() => reverse.mutate()}
      summary={
        data && (
          <p className="text-body-sm">
            {localized(data.partner)} · {t(`ptx.${data.type}` as TranslationKey)} ·{' '}
            <span className="tabular">{formatMoney(data.amount, data.currency, locale)}</span> · {formatDate(data.transactionDate, locale)}
          </p>
        )
      }
    >
      <Field label={t('ptx.reason')} required>
        <Textarea dir="auto" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}
